<template>
  <div
    role="dialog"
    class="emoji-dialog bg-white shadow-lg dark:bg-slate-900 rounded-xl border border-solid border-gray-300 dark:border-slate-800/50 box-content w-80 z-20"
    :class="triggerEl ? '' : 'absolute right-0 -top-[165px]'"
    :style="fixedStyle"
  >
    <div class="flex flex-col">
      <div class="emoji-search--wrap">
        <div class="relative">
          <fluent-icon
            icon="search"
            size="16"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none"
          />
          <input
            ref="searchbar"
            v-model="search"
            type="text"
            class="emoji-search--input focus:box-shadow-blue dark:focus:box-shadow-blue-dark"
            :placeholder="$t('EMOJI.PLACEHOLDER')"
          />
        </div>
      </div>
      <div v-if="hasNoSearch" ref="emojiItem" class="emoji-item custom-scroll">
        <h5 class="emoji-category--title">
          {{ $t(`EMOJI.${selectedKey}`) }}
        </h5>
        <div
          class="emoji--row ltr:pl-[18px] ltr:pr-[10px] rtl:pl-[10px] rtl:pr-[18px]"
        >
          <span
            v-for="item in filterEmojisByCategory"
            :key="item.slug"
            v-dompurify-html="item.emoji"
            type="span"
            class="emoji--item"
            track-by="$index"
            @click="onClick(item.emoji)"
          />
        </div>
      </div>
      <div v-else ref="emojiItem" class="emoji-item custom-scroll">
        <div v-for="category in filterAllEmojisBySearch" :key="category.slug">
          <h5 v-if="category.emojis.length > 0" class="emoji-category--title">
            {{ $t(`EMOJI.${category.name}`) }}
          </h5>
          <div
            v-if="category.emojis.length > 0"
            class="emoji--row ltr:pl-[18px] ltr:pr-[10px] rtl:pl-[10px] rtl:pr-[18px]"
          >
            <span
              v-for="item in category.emojis"
              :key="item.slug"
              v-dompurify-html="item.emoji"
              type="span"
              class="emoji--item"
              track-by="$index"
              @click="onClick(item.emoji)"
            />
          </div>
        </div>
        <div v-if="hasEmptySearchResult" class="empty-message">
          <div class="emoji-icon">
            <fluent-icon icon="emoji" size="48" />
          </div>
          <span class="empty-message--text">
            {{ $t('EMOJI.NOT_FOUND') }}
          </span>
        </div>
      </div>

      <div
        class="emoji-dialog--footer rtl:left-[unset] rtl:-right-px"
        role="menu"
      >
        <ul class="custom-scroll">
          <li :class="{ active: selectedKey === 'Search' }">
            <span class="emoji--item" @click="changeCategory('Search')">
              <span class="material-symbols-outlined text-[20px]/[20px]">
                search
              </span>
            </span>
          </li>
          <li
            v-for="category in categories"
            :key="category.slug"
            :class="{ active: selectedKey === category.name }"
            @click="changeCategory(category.name)"
          >
            <span
              class="emoji--item category-icon"
              @click="changeCategory(category.name)"
              v-html="categoryIcons[category.name] || ''"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import emojis from './emojisGroup.json';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import { getFixedDialogStyle } from 'dashboard/helper/dialogPositionHelper';
import rtlMixin from 'shared/mixins/rtlMixin';

const SEARCH_KEY = 'Search';

const CATEGORY_SVG_MAP = {
  'Smileys & Emotion': 'Smileys & People',
  'Animals & Nature': 'Animals & Nature',
  'Food & Drink': 'Food & Drink',
  'Travel & Places': 'Travel & Places',
  Activities: 'Activity',
  Objects: 'Objects',
  Symbols: 'Symbols',
  Flags: 'Flags',
};

const CATEGORY_HTML_ICONS = {
  'People & Body':
    '<span class="material-symbols-outlined text-[20px]/[20px]">emoji_people</span>',
};

export default {
  components: { FluentIcon },
  mixins: [rtlMixin],
  props: {
    onClick: {
      type: Function,
      default: () => {},
    },
    triggerEl: {
      type: HTMLElement,
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedKey: 'Search',
      emojis,
      search: '',
      categoryIcons: {},
      scrollTick: 0,
    };
  },
  computed: {
    dropdownAlign() {
      return this.isRTLView ? 'right' : 'left';
    },
    fixedStyle() {
      if (!this.triggerEl) return {};
      // eslint-disable-next-line no-unused-expressions
      this.scrollTick;
      // eslint-disable-next-line no-unused-expressions
      this.visible;
      return getFixedDialogStyle(this.triggerEl, {
        align: this.dropdownAlign,
        preferredPlacement: 'top',
        gap: 4,
      });
    },
    categories() {
      return [...this.emojis];
    },
    filterEmojisByCategory() {
      const selectedCategoryName = this.emojis.find(category =>
        category.name === this.selectedKey ? category.name : null
      );
      return selectedCategoryName?.emojis;
    },
    filterAllEmojisBySearch() {
      return this.emojis.map(category => {
        const allEmojis = category.emojis.filter(emoji =>
          emoji.slug.replaceAll('_', ' ').includes(this.search.toLowerCase())
        );
        return allEmojis.length > 0
          ? { ...category, emojis: allEmojis }
          : { ...category, emojis: [] };
      });
    },
    hasNoSearch() {
      return this.selectedKey !== SEARCH_KEY && this.search === '';
    },
    hasEmptySearchResult() {
      return this.filterAllEmojisBySearch.every(
        category => category.emojis.length === 0
      );
    },
  },
  watch: {
    search() {
      this.selectedKey = 'Search';
    },
    selectedKey() {
      return this.selectedKey === 'Search' ? this.focusSearchInput() : null;
    },
  },
  mounted() {
    this.focusSearchInput();
    this.loadCategoryIcons();
    if (this.triggerEl) {
      window.addEventListener('scroll', this.onScrollRecalculate, true);
    }
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScrollRecalculate, true);
  },
  methods: {
    onScrollRecalculate() {
      this.scrollTick += 1;
    },
    changeCategory(category) {
      this.search = '';
      this.$refs.emojiItem.scrollTo({ top: 0 });
      this.selectedKey = category;
    },
    getFirstEmojiByCategoryName(categoryName) {
      const categoryItem = this.emojis.find(category =>
        category.name === categoryName ? category : null
      );
      return categoryItem ? categoryItem.emojis[0].emoji : '';
    },
    async loadCategoryIcons() {
      const fetched = await Promise.all(
        this.emojis.map(async category => {
          if (CATEGORY_HTML_ICONS[category.name]) {
            return [category.name, CATEGORY_HTML_ICONS[category.name]];
          }
          const svgName = CATEGORY_SVG_MAP[category.name];
          if (!svgName) return [category.name, ''];
          const res = await fetch(`/emojis/${encodeURIComponent(svgName)}.svg`);
          const svg = res.ok ? await res.text() : '';
          return [category.name, svg];
        })
      );
      this.categoryIcons = Object.fromEntries(fetched);
    },
    focusSearchInput() {
      this.$refs.searchbar.focus();
    },
  },
};
</script>
<style scoped>
@tailwind components;
@layer components {
  .box-shadow-blue {
    box-shadow:
      0 0 0 1px #f0f0f0,
      0 0 4px 1px #e5e5e5;
  }

  .box-shadow-blue-dark {
    box-shadow:
      0 0 0 1px #d1d5db,
      0 0 2px 3px #4c5155;
  }
}
</style>
<style lang="scss">
@import '~dashboard/assets/scss/mixins';

.emoji--item {
  @apply bg-transparent border-0 rounded cursor-pointer text-[26px]/[26px] m-0 p-0.5;
}

.emoji--row {
  @apply box-border grid grid-cols-7 gap-x-3 gap-y-2;

  .emoji--item {
    @apply text-[26px]/[26px] p-0.5;
  }
}

.emoji-search--wrap {
  @apply m-2 sticky top-2;

  .emoji-search--input {
    @apply text-sm text-slate-800 dark:text-slate-100 h-10 m-0 pl-9 pr-3 py-2 w-full rounded-lg bg-white dark:bg-slate-800 border border-solid border-[#d1d5db] dark:border-slate-700;
  }
}

.empty-message {
  @apply items-center flex flex-col h-[280px] justify-center;

  .emoji-icon {
    @apply text-slate-200 dark:text-slate-200 mb-2;
  }
  .empty-message--text {
    @apply text-slate-200 dark:text-slate-200 text-sm font-medium;
  }
}

.emoji-item {
  @apply h-[280px] overflow-y-auto;
}

.emoji-category--title {
  @apply text-[#707174] dark:text-slate-400 text-[12px] font-semibold leading-normal m-0 pt-1 pb-2 px-[18px] uppercase tracking-wide;
}

.emoji-dialog--footer {
  @apply relative w-[322px] -left-px py-[9px] rounded-b-xl border border-solid;
  @apply border-[rgba(0,0,0,0.1)] dark:border-slate-700 px-3 bg-[#F6F6F6D9] dark:bg-[rgba(38,41,43,0.85)];
  @apply backdrop-blur-[42px];

  ul {
    @apply flex relative list-none m-0 overflow-auto py-0 px-0 justify-between;

    > li {
      @apply items-center cursor-pointer flex justify-center w-6 h-6 rounded-[4px];

      &:hover {
        @apply bg-[rgba(0,0,0,0.06)] dark:bg-slate-900;
      }
    }

    li.active {
      @apply bg-[rgba(0,0,0,0.06)] dark:bg-slate-900;

      span {
        color: #007AFF;
      }
    }
    .emoji--item {
      @apply items-center flex text-base/[16px];
    }
  }
}

.category-icon {
  svg {
    @apply w-4 h-4;
    fill: currentColor;

    path,
    rect,
    circle,
    ellipse,
    polygon,
    polyline {
      fill: currentColor;
    }
  }
}
</style>
